import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend, RadarChart, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line
} from 'recharts';
import { 
  BarChart3, TrendingUp, Sparkles, Award, Lock, BookOpen, Clock, Zap, ArrowRight,
  Brain, ShieldCheck, HeartPulse, History, AlertTriangle, ShieldClose, HelpCircle, Eye,
  RotateCcw, Activity, Trophy, Medal, Globe, MapPin, Loader2, SunMedium, CloudLightning,
  CloudSun, GraduationCap, Info, AlertCircle, CalendarDays, Layers, ListChecks, Compass,
  ShieldAlert, BrainCircuit, RefreshCw, ClipboardList, CheckCircle2
} from 'lucide-react';
import { useAuth } from '../components/AuthBridge';
import { WeeklyStudyTrends } from '../components/WeeklyStudyTrends';
import { SponsoredService } from '../services/sponsoredService';
import { MissedQuestionsService } from '../services/missedQuestionsService';
import { StorageService } from '../services/storageService';
import { predictReadiness } from '../services/geminiService';
import { US_STATES } from '../data/mockData';
import { useThemeTokens } from '../hooks/useActiveTheme';

export const AdvancedAnalytics: React.FC = () => {
  const { user, activeCert, updateUser } = useAuth();
  const tokens = useThemeTokens();
  const certId = activeCert?.id || 'cna';
  const { certIdInParams } = useParams<{ certId: string }>();
  const navigate = useNavigate();
  const [config, setConfig] = useState(SponsoredService.getConfig());
  const [isDemoData, setIsDemoData] = useState(false);
  const [selectedStudyScope, setSelectedStudyScope] = useState<'all' | 'essential' | 'supplemental'>('all');
  const [isForecasting, setIsForecasting] = useState(false);
  const [standingData, setStandingData] = useState<any>(null);
  const [isStandingLoading, setIsStandingLoading] = useState(true);

  useEffect(() => {
    const handleUpdate = () => {
      setConfig(SponsoredService.getConfig());
    };
    window.addEventListener('sponsored-config-updated', handleUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  // Fetch competitive standing data
  useEffect(() => {
    const fetchStanding = async () => {
      if (!user?.id || !certId) return;
      setIsStandingLoading(true);
      try {
        const data = await StorageService.getLeaderboardData(certId, user.id, user.state || 'Global');
        setStandingData(data);
      } catch (e) {
        console.warn("Standing Engine Offline");
      } finally {
        setIsStandingLoading(false);
      }
    };
    fetchStanding();
  }, [user?.id, certId, user?.state]);

  const isPro = user?.planTier === 'paid';

  // Fallback if super admin disabled the feature
  if (config.isAnalyticsEnabled === false) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center max-w-lg mx-auto animate-fade-in">
        <div className="p-4 bg-slate-100 dark:bg-slate-900 rounded-full text-slate-400 mb-6 border border-slate-200 dark:border-slate-800">
          <ShieldClose size={40} className="text-slate-505" />
        </div>
        <h2 className="text-2xl font-black tracking-tight text-slate-800 dark:text-slate-200">Analytics Dashboard Dormant</h2>
        <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm leading-relaxed">
          The super administrator has temporarily offline-calibrated the Advanced Student Analytics pipeline for system maintenance. Please visit other learning quadrants.
        </p>
        <button 
          onClick={() => navigate(`/${certId}/dashboard`)}
          className="mt-8 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs tracking-widest uppercase rounded-2xl transition-all shadow-lg active:scale-95"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  // --- COMPUTE LIVE ANALYTICS FROM ACTUAL USER STATE ---
  const liveAnalytics = useMemo(() => {
    const history = (user?.history || []).filter(h => h.certId === certId);
    
    if (history.length === 0) return { 
        readiness: user?.readinessForecast?.predictedScore || 0, 
        streak: 0, 
        sessions: 0, 
        focusArea: 'No Exam Data Yet', 
        masteryTrend: [], 
        domainProficiency: [],
        burnDown: [], 
        metacognition: { danger: 0, mastered: 0, honest: 0, guess: 0 },
        totalItemsProcessed: 0,
        avgQuestionTime: 0,
        passRate: 0,
        avgScore: 0
    };

    const trend = [...history].reverse().slice(-10).map((h, i) => ({
        day: `Exam ${i + 1}`,
        score: h.score,
        avgTime: h.avgQuestionTime || 40,
        readiness: Math.round((h.score * 0.75) + (h.passed ? 25 : 10))
    }));

    const avgScore = Math.round(history.reduce((acc, curr) => acc + (curr.score || 0), 0) / history.length);
    const passRate = Math.round((history.filter(h => h.passed).length / history.length) * 100);
    const historyReadiness = Math.round((avgScore * 0.6) + (passRate * 0.4));
    
    const readiness = history.length > 0 ? historyReadiness : (user?.readinessForecast?.predictedScore || 0);

    const domainMap: Record<string, { total: number, count: number, highest: number }> = {};
    const metacognition = { danger: 0, mastered: 0, honest: 0, guess: 0 };
    let totalItemsTracked = 0;
    let totalTimeSum = 0;
    let timeCount = 0;

    history.forEach(session => {
        if (session.avgQuestionTime) {
          totalTimeSum += session.avgQuestionTime;
          timeCount++;
        }
        const breakdown = session.categoryBreakdown || session.masteryByDomain;
        if (breakdown && Array.isArray(breakdown)) {
            breakdown.forEach((d: any) => {
                const cleanName = (d.name || d.category || '').replace(' Skills', '').replace(' Procedures', '');
                if (cleanName) {
                    if (!domainMap[cleanName]) domainMap[cleanName] = { total: 0, count: 0, highest: 0 };
                    domainMap[cleanName].total += (d.score || 0);
                    domainMap[cleanName].count += 1;
                    domainMap[cleanName].highest = Math.max(domainMap[cleanName].highest, d.score || 0);
                }
            });
        }

        const questions = session.questions || [];
        const userAnswers = session.userAnswers || {};
        const confMap = session.confidenceMap || {};

        questions.forEach((q: any) => {
            totalItemsTracked++;
            const userAns = userAnswers[q._id];
            const correctOptionObj = q.options?.find((o: any) => 
               o.isCorrect === true || String(o.isCorrect).toLowerCase() === 'true' || Number(o.isCorrect) === 1
            );
            const correctOption = correctOptionObj?._id;
            
            const isCorrect = q.type === 'ordering' 
                ? (userAns === q.options?.map((o: any) => o._id).join(','))
                : (String(userAns) === String(correctOption));
            
            const conf = confMap[q._id] || 'high';

            if (isCorrect && conf === 'high') metacognition.mastered++;
            else if (isCorrect && conf === 'low') metacognition.honest++;
            else if (!isCorrect && conf === 'high') metacognition.danger++;
            else if (!isCorrect && conf === 'low') metacognition.guess++;
        });
    });

    const domainProficiency = Object.entries(domainMap).map(([name, data]) => ({
        name,
        value: Math.round(data.total / data.count),
    })).sort((a, b) => a.value - b.value);

    const focusArea = domainProficiency.length > 0 
        ? domainProficiency[0].name 
        : 'Emergency Protocol Procedures';

    const burnDown = history.slice().reverse().reduce((acc: any[], curr: any, idx: number) => {
        const prevTotal = idx > 0 ? acc[idx-1].total : 0;
        acc.push({
            day: `Day ${idx + 1}`,
            total: prevTotal + (curr.totalQuestions || 0)
        });
        return acc;
    }, []);

    const metaPercentages = {
      mastered: totalItemsTracked ? Math.round((metacognition.mastered / totalItemsTracked) * 100) : 0,
      honest: totalItemsTracked ? Math.round((metacognition.honest / totalItemsTracked) * 100) : 0,
      danger: totalItemsTracked ? Math.round((metacognition.danger / totalItemsTracked) * 100) : 0,
      guess: totalItemsTracked ? Math.round((metacognition.guess / totalItemsTracked) * 100) : 0
    };

    const overallAvgTime = timeCount > 0 ? Math.round(totalTimeSum / timeCount) : 40;

    return {
        readiness,
        sessions: history.length,
        focusArea,
        masteryTrend: trend,
        domainProficiency: domainProficiency.slice(0, 5),
        burnDown,
        metacognition: metaPercentages,
        totalItemsProcessed: totalItemsTracked,
        avgQuestionTime: overallAvgTime,
        passRate,
        avgScore
    };
  }, [user, certId]);


  // Determine active metrics setup based on demo mode or live state
  const analytics = useMemo(() => {
    const defaultCategories = [
      'Basic Clinical Skills',
      'Hygiene & Safety Care',
      'Patient Dignity Protocols',
      'Clinical Infection Controls',
      'Critical Communication'
    ];
    const categories = activeCert?.categories?.length ? activeCert.categories : defaultCategories;
    
    const makeDomainProficiencies = (baseScores: number[]) => {
      return categories.slice(0, Math.max(4, baseScores.length)).map((cat, idx) => {
        const score = baseScores[idx % baseScores.length] || 75;
        return { name: cat, value: score };
      });
    };

    const demoProficiency = makeDomainProficiencies([92, 87, 85, 78, 64]);
    const demoFocusArea = demoProficiency.length > 0 ? demoProficiency[demoProficiency.length - 1].name : 'Clinical Protocol';

    if (isDemoData) {
      return {
        readiness: 91,
        sessions: 12,
        focusArea: demoFocusArea,
        masteryTrend: [
          { day: 'Day 1', score: 62, avgTime: 55, readiness: 58 },
          { day: 'Day 2', score: 65, avgTime: 52, readiness: 61 },
          { day: 'Day 3', score: 68, avgTime: 53, readiness: 64 },
          { day: 'Day 4', score: 71, avgTime: 48, readiness: 69 },
          { day: 'Day 5', score: 75, avgTime: 46, readiness: 72 },
          { day: 'Day 6', score: 74, avgTime: 45, readiness: 74 },
          { day: 'Day 7', score: 78, avgTime: 43, readiness: 77 },
          { day: 'Day 8', score: 81, avgTime: 41, readiness: 82 },
          { day: 'Day 9', score: 84, avgTime: 38, readiness: 85 },
          { day: 'Day 10', score: 87, avgTime: 35, readiness: 91 },
        ],
        domainProficiency: demoProficiency,
        burnDown: [
          { day: 'Day 1', total: 10 },
          { day: 'Day 2', total: 25 },
          { day: 'Day 3', total: 42 },
          { day: 'Day 4', total: 60 },
          { day: 'Day 5', total: 85 },
          { day: 'Day 6', total: 110 },
          { day: 'Day 7', total: 135 },
          { day: 'Day 8', total: 160 },
          { day: 'Day 9', total: 185 },
          { day: 'Day 10', total: 220 },
        ],
        metacognition: { danger: 8, mastered: 72, honest: 12, guess: 8 },
        totalItemsProcessed: 220,
        avgQuestionTime: 35,
        passRate: 90,
        avgScore: 84
      };
    }

    // Live Mode: Return exact metrics calculated from actual user activity history
    return liveAnalytics;
  }, [isDemoData, liveAnalytics, activeCert]);

  const missedCount = MissedQuestionsService.getCount();

  // Handle competitive standings benchmarking based on state or globally
  const leaderboardState = useMemo(() => {
    if (isDemoData) {
      return {
        global: { rank: 142, percentile: 4.8 },
        state: { rank: 12, percentile: 3.2 }
      };
    }
    return {
      global: {
        rank: standingData?.global?.rank || (analytics.sessions > 0 ? 284 : '--'),
        percentile: standingData?.global?.percentile || (analytics.sessions > 0 ? 14.5 : 0)
      },
      state: {
        rank: standingData?.state?.rank || (analytics.sessions > 0 ? 26 : '--'),
        percentile: standingData?.state?.percentile || (analytics.sessions > 0 ? 11.2 : 0)
      }
    };
  }, [isDemoData, standingData, analytics.sessions]);

  // Encouraging and actionable tips based on academic performance metrics
  const encouragementTips = useMemo(() => {
    if (analytics.sessions === 0) {
      return {
        title: "Welcome to Analytics Pro!",
        desc: "Your student activity tracking engine is online and ready. Take your first practice quiz or simulated exam to generate your real-time readiness score, domain mastery breakdown, and cognitive performance vector.",
        actionLabel: "Take First Practice Quiz",
        actionPath: "practice"
      };
    }

    const score = analytics.readiness;
    const focus = analytics.focusArea;

    if (score >= 85) {
      return {
        title: "State Board Accreditation Safe Zone Enforced!",
        desc: `You are currently matching standard board certified passing ratios, with a strong ${score}% predicted readiness. Keep the momentum high. Ensure you remediate minor logical traps regularly in "${focus}" to secure your perfect score!`,
        actionLabel: "Take Next Mini Exam",
        actionPath: "exam"
      };
    } else if (score >= 70 && score < 85) {
      return {
        title: "Outstanding Consistency! Almost Passed!",
        desc: `You are on the absolute cusp of a dominant pass rating! Your global readiness of ${score}% can easily jump above 85% by sharpening high fidelity protocols in "${focus}". Practice 10 more questions on this topic today!`,
        actionLabel: "Practice Topic Drills",
        actionPath: "practice"
      };
    } else {
      return {
        title: "Tremendous Initial Foundation. Keep Calibrating!",
        desc: `Step-by-step is the path to medical mastery. Since you have processing benchmarks at ${score}%, we highly recommend completing a few drills focusing on "${focus}" first. Doing so will lift your readiness rating by +10%!`,
        actionLabel: "Practice Easy Quizzes",
        actionPath: "practice"
      };
    }
  }, [analytics]);

  // Clean data-mappings matching screenshots perfectly
  const trendData = useMemo(() => {
    return (analytics.masteryTrend || []).map((t, idx) => ({
      ...t,
      label: t.day.startsWith('Day') ? t.day : `Day ${idx + 1}`
    }));
  }, [analytics.masteryTrend]);

  const last30DaysTrendData = useMemo(() => {
    const data = [];
    const history = (user?.history || []).filter(h => h.certId === certId);
    const today = new Date();
    
    // In demo mode, show high-sample trend curve
    if (isDemoData) {
      for (let i = 29; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const demoProgress = (29 - i) / 29; 
        const baseCurve = 62 + (28 * Math.pow(demoProgress, 0.75)); 
        const noise = Math.sin(i * 1.8) * 3 + Math.cos(i * 2.5) * 1.5;
        const score = Math.max(55, Math.min(98, Math.round(baseCurve + noise)));
        data.push({
          date: dateStr,
          score,
          isMock: true,
          actualScore: score,
          practiceTarget: 75,
        });
      }
      return data;
    }

    // Live Mode: Only display data for actual completed test sessions on each date
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const dayStart = new Date(d);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(d);
      dayEnd.setHours(23, 59, 59, 999);

      const itemsOnThisDay = history.filter(h => {
        if (!h.timestamp && !h.completedAt && !h.date) return false;
        const hDate = new Date(h.timestamp || h.completedAt || h.date);
        return hDate >= dayStart && hDate <= dayEnd;
      });

      if (itemsOnThisDay.length > 0) {
        const totalDayScore = itemsOnThisDay.reduce((acc, curr) => acc + (curr.score || 0), 0);
        const dayAvgScore = Math.round(totalDayScore / itemsOnThisDay.length);
        data.push({
          date: dateStr,
          score: dayAvgScore,
          isMock: false,
          actualScore: dayAvgScore,
          practiceTarget: 75,
        });
      } else if (history.length > 0) {
        data.push({
          date: dateStr,
          score: liveAnalytics.avgScore || 0,
          isMock: false,
          actualScore: undefined,
          practiceTarget: 75,
        });
      } else {
        data.push({
          date: dateStr,
          score: 0,
          isMock: false,
          actualScore: undefined,
          practiceTarget: 75,
        });
      }
    }

    return data;
  }, [user, certId, isDemoData, liveAnalytics.avgScore]);

  const conceptMasteryData = useMemo(() => {
    const defaultCategories = [
      'Basic Clinical Skills',
      'Hygiene & Safety Care',
      'Patient Dignity Protocols',
      'Clinical Infection Controls',
      'Critical Communication'
    ];
    const categories = activeCert?.categories?.length ? activeCert.categories : defaultCategories;
    const colors = ['#10b981', '#f43f5e', '#8b5cf6', '#0ea5e9', '#f59e0b'];

    if (isDemoData) {
      const demoScores = [92, 87, 85, 78, 64];
      return categories.slice(0, 5).map((cat, idx) => ({
        name: cat.toUpperCase(),
        value: demoScores[idx % demoScores.length],
        color: colors[idx % colors.length]
      }));
    }

    if (analytics.domainProficiency && analytics.domainProficiency.length > 0) {
      return analytics.domainProficiency.map((item, idx) => ({
        name: item.name.toUpperCase(),
        value: item.value,
        color: colors[idx % colors.length]
      }));
    }

    // Real new user with 0 completed test sessions: 0% per category
    return categories.slice(0, 5).map((cat, idx) => ({
      name: cat.toUpperCase(),
      value: 0,
      color: colors[idx % colors.length]
    }));
  }, [isDemoData, analytics.domainProficiency, activeCert]);

  const globalAccuracyStr = useMemo(() => {
    if (isDemoData) return '80.4%';
    if (analytics.sessions === 0) return '0%';
    if (conceptMasteryData.length === 0) return '0%';
    const sum = conceptMasteryData.reduce((acc, curr) => acc + curr.value, 0);
    return (sum / conceptMasteryData.length).toFixed(1) + '%';
  }, [isDemoData, analytics.sessions, conceptMasteryData]);

  const engagementDataset = useMemo(() => {
    if (isDemoData) {
      const rawEngagementData = [
        { name: 'Quiz Engine', progress: 78, target: 120, type: 'essential' },
        { name: 'Study Guide', progress: 74, target: 100, type: 'essential' },
        { name: 'Exam Sim', progress: 4, target: 7, type: 'essential' },
        { name: 'Missed Vault', progress: 22, target: 30, type: 'supplemental' },
        { name: 'Audio Hub', progress: 110, target: 180, type: 'supplemental' },
        { name: 'Math Lab', progress: 18, target: 25, type: 'supplemental' },
      ];
      if (selectedStudyScope === 'all') return rawEngagementData;
      return rawEngagementData.filter(d => d.type === selectedStudyScope);
    }

    // Live Mode: Compute actual activity progress dynamically
    const history = (user?.history || []).filter(h => h.certId === certId);
    
    const quizQuestionsAnswered = history
      .filter(h => h.mode !== 'simulator' && !h.isExam)
      .reduce((acc, h) => acc + (h.totalQuestions || h.questions?.length || 0), 0);

    const flashcardsMastered = user?.stats?.flashcardsMastered || 0;

    const examSimsCompleted = history
      .filter(h => h.mode === 'simulator' || h.isExam || h.examType === 'simulator').length;

    const missedVaultCount = MissedQuestionsService.getCount();

    const audioListened = user?.stats?.audioListenedCount || 0;

    const mathQuestionsCompleted = history
      .filter(h => h.category?.toLowerCase().includes('math') || h.title?.toLowerCase().includes('math'))
      .reduce((acc, h) => acc + (h.totalQuestions || h.questions?.length || 0), 0);

    const liveEngagementData = [
      { name: 'Quiz Engine', progress: quizQuestionsAnswered, target: 100, type: 'essential' },
      { name: 'Study Guide', progress: flashcardsMastered, target: 50, type: 'essential' },
      { name: 'Exam Sim', progress: examSimsCompleted, target: 5, type: 'essential' },
      { name: 'Missed Vault', progress: missedVaultCount, target: 20, type: 'supplemental' },
      { name: 'Audio Hub', progress: audioListened, target: 15, type: 'supplemental' },
      { name: 'Math Lab', progress: mathQuestionsCompleted, target: 25, type: 'supplemental' },
    ];

    if (selectedStudyScope === 'all') return liveEngagementData;
    return liveEngagementData.filter(d => d.type === selectedStudyScope);
  }, [isDemoData, user, certId, selectedStudyScope]);

  const cognitiveRadarData = useMemo(() => {
    if (isDemoData) {
      return [
        { subject: 'Answer Timings', A: 82, fullMark: 100 },
        { subject: 'Clinical Safety', A: 96, fullMark: 100 },
        { subject: 'Error Minimization', A: 75, fullMark: 100 },
        { subject: 'Retrieval Velocity', A: 88, fullMark: 100 },
        { subject: 'Retention Accuracy', A: 84, fullMark: 100 },
        { subject: 'Logic Synergy', A: 90, fullMark: 100 }
      ];
    }

    if (analytics.sessions === 0) {
      return [
        { subject: 'Answer Timings', A: 0, fullMark: 100 },
        { subject: 'Clinical Safety', A: 0, fullMark: 100 },
        { subject: 'Error Minimization', A: 0, fullMark: 100 },
        { subject: 'Retrieval Velocity', A: 0, fullMark: 100 },
        { subject: 'Retention Accuracy', A: 0, fullMark: 100 },
        { subject: 'Logic Synergy', A: 0, fullMark: 100 }
      ];
    }

    const retentionAcc = analytics.avgScore || 0;
    const passRate = analytics.passRate || 0;
    const avgTime = analytics.avgQuestionTime || 40;

    let timingsScore = 80;
    if (avgTime > 0) {
      if (avgTime <= 45) timingsScore = 90;
      else if (avgTime <= 60) timingsScore = 75;
      else timingsScore = 60;
    }

    const safetyDomain = analytics.domainProficiency?.find(d => 
      d.name.toLowerCase().includes('safety') || d.name.toLowerCase().includes('infection')
    );
    const safetyScore = safetyDomain ? safetyDomain.value : retentionAcc;

    const velocityScore = Math.min(100, Math.round(retentionAcc * (45 / Math.max(20, avgTime))));
    const logicSynergy = Math.round((retentionAcc * 0.6) + (passRate * 0.4));

    return [
      { subject: 'Answer Timings', A: timingsScore, fullMark: 100 },
      { subject: 'Clinical Safety', A: safetyScore, fullMark: 100 },
      { subject: 'Error Minimization', A: retentionAcc, fullMark: 100 },
      { subject: 'Retrieval Velocity', A: velocityScore, fullMark: 100 },
      { subject: 'Retention Accuracy', A: retentionAcc, fullMark: 100 },
      { subject: 'Logic Synergy', A: logicSynergy, fullMark: 100 }
    ];
  }, [isDemoData, analytics]);

  // Recalibrate AI logic matching standard dashboard
  const handleRefreshForecast = useCallback(async () => {
    const history = (user?.history || []).filter(h => h.certId === certId);
    if (!history.length) {
      alert("Simulation data insufficient. Please complete a practice board or quiz first to seed the AI predictive engine.");
      return;
    }
    
    setIsForecasting(true);
    try {
      let diffDays = 30; 
      if (user?.studyPlan?.targetDate) {
        const target = new Date(user.studyPlan.targetDate);
        const diffTime = Math.abs(target.getTime() - Date.now());
        diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }
      
      const forecast = await predictReadiness(
        history, 
        user?.stats?.studyHours || 0, 
        diffDays,
        activeCert?.name || 'CNA'
      );
      
      await updateUser({
        readinessForecast: {
          ...forecast,
          lastRun: new Date().toISOString()
        }
      });
    } catch (e) {
      console.error("Forecast recalibration error:", e);
    } finally {
      setIsForecasting(false);
    }
  }, [user, certId, activeCert, updateUser]);

  const fullStateName = useMemo(() => {
    const sObj = US_STATES.find(s => s.code === user?.state);
    return sObj ? sObj.name : (user?.state || 'California');
  }, [user?.state]);

  const COLORS = ['#10b981', '#6366f1', '#f59e0b', '#f43f5e'];

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      {/* Dynamic/Decorative Premium Page Header */}
      <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 p-8 ${tokens.heroBannerBg} rounded-[2.5rem] border border-white/10 shadow-xl text-white relative overflow-hidden backdrop-blur-md`}>
        <div className="absolute right-0 top-0 w-80 h-80 bg-white/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 bottom-0 w-60 h-60 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        
        <div className="space-y-2 relative z-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 ${tokens.badgeBg} rounded-xl border border-white/10 shadow-xs`}>
            <Sparkles size={14} className={tokens.accentText} />
            <span className={`text-[10px] font-black tracking-widest uppercase ${tokens.accentText}`}>Advanced Intelligence Station</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight uppercase leading-none">
            Advanced Analytics <span className={tokens.accentText}>Pro</span>
          </h1>
          <p className="text-slate-300 font-bold text-xs tracking-wider uppercase">
            Accurate, real-time feedback loops of logical accuracy, standing profiles, and cognitive confidence
          </p>
        </div>

        <div className="flex flex-wrap gap-3 relative z-10 shrink-0">
          {!isPro && (
            <button 
              onClick={() => navigate(`/${certId}/elite`)}
              className="flex items-center gap-2 px-5 py-3.5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-black text-xs uppercase tracking-widest rounded-2xl transition-all shadow-[0_5px_20px_rgba(245,158,11,0.35)] hover:-translate-y-0.5 active:scale-95"
            >
              <Lock size={14} className="fill-slate-950 text-slate-950 shrink-0" />
              <span>Unlock Elite Premium</span>
            </button>
          )}
          <button 
            onClick={() => setIsDemoData(!isDemoData)}
            className="flex items-center gap-2 px-5 py-3.5 bg-white/10 dark:bg-slate-900/40 hover:bg-white/15 text-slate-200 font-black text-[10px] tracking-widest uppercase rounded-2xl transition-all border border-white/10"
          >
            <RotateCcw size={12} className="shrink-0" />
            <span>{isDemoData ? "Use My Live Stats" : "View High Sample Stats"}</span>
          </button>
        </div>
      </div>

      {/* Live Data Notice if user has no data yet */}
      {liveAnalytics.sessions === 0 && !isDemoData && (
        <div className="bg-emerald-50/70 border border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/40 rounded-[2rem] p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left shadow-xs">
          <div className="p-3 bg-emerald-500/10 text-emerald-600 rounded-full">
            <Info size={24} />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-extrabold text-emerald-900 dark:text-emerald-300 uppercase tracking-tight">Live Student Analytics Active</h4>
            <p className="text-xs text-emerald-800/80 dark:text-slate-400 font-semibold mt-1">
              Welcome! You haven't completed any practice quizzes or simulated exams yet. Complete your first practice test to track your real-time score trajectory, or click "View High Sample Stats" above to preview sample data.
            </p>
          </div>
        </div>
      )}

      {/* Primary Key Performance Indicators */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Metric 1 */}
        <div className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800/80 p-6 rounded-[2rem] flex flex-col justify-between shadow-xs ${tokens.cardHoverBorder} transition-all group relative overflow-hidden`}>
          <div className="space-y-4 relative z-10">
            <div className={`p-3 ${tokens.badgeBg} ${tokens.accentText} rounded-xl w-fit`}>
              <Zap size={22} className={tokens.accentText} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Global Exam Readiness</p>
              <h3 className="text-2xl font-black mt-1 text-slate-900 dark:text-slate-100 tabular-nums">
                {analytics.readiness}%
              </h3>
            </div>
          </div>
          <div className="text-[9px] font-bold text-slate-400 mt-4 uppercase border-t border-slate-100 dark:border-slate-800 pt-3">
            Target Passing Standard: 85%
          </div>
        </div>

        {/* Metric 2 */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800/80 p-6 rounded-[2rem] flex flex-col justify-between shadow-xs hover:border-emerald-400/30 transition-all group relative overflow-hidden">
          <div className="absolute right-[-10px] top-[-10px] w-20 h-20 bg-emerald-50 dark:bg-emerald-950/20 rounded-full blur-lg group-hover:scale-125 transition-transform" />
          <div className="space-y-4 relative z-10">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 rounded-xl w-fit">
              <ListChecks size={22} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Simulated Sessions</p>
              <h3 className="text-2xl font-black mt-1 text-slate-900 dark:text-slate-100 tabular-nums">
                {analytics.sessions} <span className="text-xs text-slate-400 font-semibold">Exam Trials</span>
              </h3>
            </div>
          </div>
          <div className="text-[9px] font-bold text-slate-400 mt-4 uppercase border-t border-slate-100 dark:border-slate-800 pt-3">
            Real progress accuracy tracked
          </div>
        </div>

        {/* Metric 3 */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800/80 p-6 rounded-[2rem] flex flex-col justify-between shadow-xs hover:border-amber-400/30 transition-all group relative overflow-hidden">
          <div className="absolute right-[-10px] top-[-10px] w-20 h-20 bg-amber-50 dark:bg-amber-950/20 rounded-full blur-lg group-hover:scale-125 transition-transform" />
          <div className="space-y-4 relative z-10">
            <div className="p-3 bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 rounded-xl w-fit">
              <Activity size={22} className="text-amber-600" />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Nodes Processed</p>
              <h3 className="text-2xl font-black mt-1 text-slate-900 dark:text-slate-100 tabular-nums">
                {analytics.totalItemsProcessed} <span className="text-xs text-slate-400 font-semibold">Questions</span>
              </h3>
            </div>
          </div>
          <div className="text-[9px] font-bold text-slate-400 mt-4 uppercase border-t border-slate-100 dark:border-slate-800 pt-3">
            Cumulative problem database items
          </div>
        </div>

        {/* Metric 4 */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-805/80 p-6 rounded-[2rem] flex flex-col justify-between shadow-xs hover:border-sky-400/30 transition-all group relative overflow-hidden">
          <div className="absolute right-[-10px] top-[-10px] w-20 h-20 bg-sky-50 dark:bg-sky-950/20 rounded-full blur-lg group-hover:scale-125 transition-transform" />
          <div className="space-y-4 relative z-10">
            <div className="p-3 bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-400 rounded-xl w-fit">
              <HeartPulse size={22} className="text-sky-600 animate-pulse" />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">Registry Weak Area</p>
              <h3 className="text-base font-black mt-1.5 text-rose-500 dark:text-rose-400 truncate tracking-tight">
                {analytics.focusArea.replace(' Procedures', '')}
              </h3>
            </div>
          </div>
          <div className="text-[9px] font-bold text-slate-400 mt-4 uppercase border-t border-slate-100 dark:border-slate-800 pt-3">
            Targeting this area will raise readiness
          </div>
        </div>
      </div>

      {/* Weekly Study Trends Analytics */}
      <WeeklyStudyTrends />

      {/* 1. EXAM FORECAST WITH AI CALIBRATOR */}
      <div className={`${tokens.heroBannerBg} rounded-[3rem] p-8 md:p-12 text-white relative overflow-hidden border border-white/10 shadow-2xl group`}>
        <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:rotate-12 transition-transform duration-1000">
          <GraduationCap size={200} />
        </div>
        
        <div className="relative z-10 flex flex-col justify-between">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-10">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-2xl shadow-xl transition-all duration-500 ${
                analytics.readiness >= 85 ? 'bg-amber-500' : tokens.primaryBtn
              }`}>
                {analytics.readiness >= 85 ? <SunMedium size={32} /> : <CloudSun size={32} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-2xl font-black tracking-tight">Accreditation Readiness Forecast</h3>
                  {isPro && <Sparkles size={16} className="text-amber-400 animate-pulse" />}
                </div>
                <p className="text-[10px] font-black text-slate-300 tracking-[0.4em] mt-1 uppercase">
                  {isPro ? 'Recursive AI Logic Modules (Elite Fidelity)' : 'Standard Accreditation Prediction Mode'}
                </p>
              </div>
            </div>

            {isPro ? (
              <button 
                onClick={handleRefreshForecast}
                disabled={isForecasting}
                className={`${tokens.primaryBtn} px-6 py-3 rounded-xl border border-white/20 text-[10px] font-black tracking-widest uppercase transition-all flex items-center gap-2 shadow-lg active:scale-95 text-white disabled:opacity-50`}
              >
                {isForecasting ? <Loader2 size={14} className="animate-spin text-white" /> : <RefreshCw size={14} className="text-white" />} 
                <span>Recalibrate Forecast</span>
              </button>
            ) : (
              <button 
                onClick={() => navigate(`/${certId}/elite`)}
                className="bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl border border-white/10 text-[10px] font-black tracking-widest uppercase transition-all flex items-center gap-2"
              >
                <Lock size={12} className="text-amber-400 fill-amber-400" /> 
                <span>Unlock Advanced Calibration</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="text-center lg:text-left">
              <p className={`text-5xl md:text-7xl font-black tracking-tighter tabular-nums ${tokens.accentText}`}>
                {analytics.readiness}%
              </p>
              <p className="text-[10px] font-black text-slate-300 tracking-widest mt-2 uppercase">Predicted Board Score</p>
            </div>
            <div className="lg:col-span-2 p-6 bg-white/5 border border-white/10 rounded-3xl space-y-4">
              <div className="flex items-center gap-2">
                <Brain size={14} className={tokens.accentText}/>
                <span className="text-[10px] font-black tracking-widest text-slate-300 uppercase">Predictive Student Rationale</span>
              </div>
              <p className="text-sm md:text-base font-bold leading-relaxed text-slate-200 italic">
                "{encouragementTips.desc}"
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Intelligence Bento Panels */}
      <div className="space-y-8">
        
        {/* Row 1: Accreditation Readiness Trend & Concept Mastery Spread (Responsive split) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Widget 1: Accreditation Readiness Trend */}
          <div className="lg:col-span-12 xl:col-span-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] shadow-xs flex flex-col justify-between min-h-[460px] group relative overflow-hidden transition-all duration-350 hover:shadow-md">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 id="readiness-trend-title" className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">Accreditation Readiness Trend</h3>
                  <p className="text-slate-400 text-xs font-semibold mt-0.5">Calculated across your last 10 simulated trials</p>
                </div>
                <div className="px-3.5 py-1.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-650 dark:text-indigo-400 font-extrabold text-[10px] tracking-widest rounded-xl flex items-center gap-1 shrink-0">
                  <span>↗ PASSING TARGET EXCEEDED</span>
                </div>
              </div>

              <div className="h-64 mt-8">
                {trendData && trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="readinessTrendG" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.12}/>
                          <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:stroke-slate-800" />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fontWeight: 'bold' }} stroke="#94a3b8" />
                      <YAxis domain={[40, 100]} ticks={[40, 55, 70, 85, 100]} tick={{ fontSize: 9, fontWeight: 'bold' }} stroke="#94a3b8" />
                      <Tooltip contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }} />
                      <Area type="monotone" dataKey="readiness" stroke="#4f46e5" strokeWidth={3.5} fillOpacity={1} fill="url(#readinessTrendG)" name="AI Readiness Index (%)" />
                      <Area type="monotone" dataKey="score" stroke="#10b981" strokeWidth={2.5} fill="none" name="Simulated Trial Score (%)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center opacity-30 gap-2">
                    <Activity size={32} />
                    <p className="text-xs font-bold tracking-wider uppercase">Evaluating metrics...</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Custom Legend to match screenshot 1 perfectly */}
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-slate-400 pt-5 mt-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#4f46e5]" />
                <span className="text-slate-500 dark:text-slate-350">AI Readiness Index (%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#10b981]" />
                <span className="text-slate-500 dark:text-slate-350">Simulated Trial Score (%)</span>
              </div>
            </div>
          </div>

          {/* Widget 2: Concept Mastery Spread (Donut chart & List) */}
          <div className="lg:col-span-12 xl:col-span-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-between min-h-[460px] group relative overflow-hidden transition-all duration-350 hover:shadow-md">
            <div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 id="mastery-spread-title" className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">Concept Mastery Spread</h3>
                  <p className="text-slate-400 text-xs font-semibold mt-0.5">Ratio of correctly answered items by topic category</p>
                </div>
              </div>

              {/* Donut Chart with centerpiece matching screenshot 1 */}
              <div className="relative h-48 mt-6">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={conceptMasteryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={82}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {conceptMasteryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '1.2rem', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center pointer-events-none">
                  <span className="text-3xl font-black text-slate-900 dark:text-white tabular-nums tracking-tighter leading-none">
                    {globalAccuracyStr}
                  </span>
                  <span className="text-[9px] font-bold text-slate-400 tracking-wider uppercase mt-1 leading-none">
                    Global Accuracy
                  </span>
                </div>
              </div>

              {/* Custom styled elements list beneath the Donut Chart */}
              <div className="space-y-3.5 mt-6 px-1.5">
                {conceptMasteryData.map((topic, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs font-black uppercase tracking-wider">
                    <div className="flex items-center gap-2.5">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 animate-pulse" style={{ backgroundColor: topic.color }} />
                      <span className="text-slate-600 dark:text-slate-400">{topic.name}</span>
                    </div>
                    <span className="text-slate-900 dark:text-white font-black">{topic.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Performance Trend & Active Quadrant Engagement */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Widget 3: Performance Trend Line Chart */}
          <div className="lg:col-span-12 xl:col-span-7 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-between min-h-[480px] group relative overflow-hidden transition-all duration-350 hover:shadow-md">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-650 dark:text-indigo-400 rounded-xl">
                    <TrendingUp size={20} className="text-indigo-650 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 id="performance-trend-title" className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">Performance Trend (Last 30 Days)</h3>
                    <p className="text-slate-400 text-xs font-semibold mt-0.5">Plotting cumulative quiz scores to track continuous concept retention & cognitive improvement</p>
                  </div>
                </div>
                {!isPro && (
                  <span className="px-2.5 py-1 bg-amber-100 dark:bg-amber-955 text-amber-800 dark:text-amber-400 font-black text-[9px] tracking-widest uppercase rounded-lg border border-amber-200/55 shadow-xs">
                    Basic Mode
                  </span>
                )}
              </div>

              <div className="h-64 mt-8">
                {last30DaysTrendData && last30DaysTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={last30DaysTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-800" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 9, fontWeight: 'bold' }} 
                        stroke="#94a3b8" 
                        interval={4}
                      />
                      <YAxis 
                        domain={[40, 100]} 
                        ticks={[40, 55, 70, 85, 100]} 
                        tick={{ fontSize: 9, fontWeight: 'bold' }} 
                        stroke="#94a3b8" 
                      />
                      <Tooltip 
                        contentStyle={{ 
                          borderRadius: '1.2rem', 
                          border: 'none', 
                          boxShadow: '0 10px 30px rgba(0,0,0,0.1)', 
                          backgroundColor: '#1e293b', 
                          color: '#fff' 
                        }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const dataItem = payload[0].payload;
                            return (
                              <div className="bg-slate-900 text-white p-4 rounded-2xl border border-slate-850 shadow-xl space-y-1.5 animate-fade-in border-l-4 border-indigo-500">
                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{dataItem.date}</p>
                                <p className="text-sm font-black text-indigo-400 flex items-center justify-between gap-6">
                                  <span>Quiz Score:</span>
                                  <span className="text-white font-black">{dataItem.score}%</span>
                                </p>
                                <p className="text-[10px] font-bold text-slate-400">
                                  State Certification Benchmark: {dataItem.practiceTarget}%
                                </p>
                                <p className="text-[9px] font-black tracking-widest text-emerald-400 uppercase mt-1">
                                  {dataItem.score >= 85 ? "🎯 Mastered Range" : dataItem.score >= 70 ? "⚡ Promising Trajectory" : "📚 Continuous Learning"}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="practiceTarget" 
                        stroke="#f43f5e" 
                        strokeWidth={1.5} 
                        strokeDasharray="5 5" 
                        dot={false}
                        activeDot={false}
                        name="Accredited Standard Benchmark (75%)" 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#6366f1" 
                        strokeWidth={4.5} 
                        dot={(props: any) => {
                          const { cx, cy, payload, index } = props;
                          if (payload.actualScore !== undefined || index === 0 || index === 29) {
                            return (
                              <circle 
                                key={`dot-${index}`} 
                                cx={cx} 
                                cy={cy} 
                                r={payload.actualScore ? 6 : 4} 
                                fill={payload.actualScore ? "#10b981" : "#6366f1"} 
                                stroke="#fff" 
                                strokeWidth={2} 
                                className="shadow-lg filter drop-shadow-[0_2px_4px_rgba(99,102,241,0.5)]"
                              />
                            );
                          }
                          return null;
                        }}
                        activeDot={{ r: 8, strokeWidth: 0, fill: '#10b981' }}
                        name="Quiz Retained Scores (%)" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center opacity-30 gap-2">
                    <Activity size={32} />
                    <p className="text-xs font-bold tracking-wider uppercase">Loading performance trends...</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-2 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-slate-400 pt-5 mt-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#6366f1]" />
                <span className="text-slate-500 dark:text-slate-350">Quiz Score Profile (%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-0.5 border-t-2 border-dashed border-[#f43f5e]" />
                <span className="text-slate-500 dark:text-slate-350 font-black text-rose-500">Board Benchmark Target (75%)</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]" />
                <span className="text-slate-400">Live Completed Scores</span>
              </div>
            </div>
          </div>

          {/* Widget 4: Active Quadrant Engagement bar-chart */}
          <div className="lg:col-span-12 xl:col-span-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-between min-h-[480px] group relative overflow-hidden transition-all duration-350 hover:shadow-md">
            <div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 id="engagement-quadrant-title" className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">Active Quadrant Engagement</h3>
                  <p className="text-slate-400 text-xs font-semibold mt-0.5">Total question items completed against targets</p>
                </div>
                
                {/* All Study Option Interactive Filter */}
                <div className="flex items-center bg-slate-105 p-1 rounded-xl border border-slate-200 dark:bg-slate-950 dark:border-slate-800 self-start">
                  <button 
                    onClick={() => setSelectedStudyScope('all')}
                    className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all ${
                      selectedStudyScope === 'all' 
                        ? tokens.activeTab
                        : tokens.inactiveTab
                    }`}
                  >
                    All Options
                  </button>
                  <button 
                    onClick={() => setSelectedStudyScope('essential')}
                    className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all ${
                      selectedStudyScope === 'essential' 
                        ? tokens.activeTab
                        : tokens.inactiveTab
                    }`}
                  >
                    Exams
                  </button>
                  <button 
                    onClick={() => setSelectedStudyScope('supplemental')}
                    className={`px-3 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all ${
                      selectedStudyScope === 'supplemental' 
                        ? tokens.activeTab
                        : tokens.inactiveTab
                    }`}
                  >
                    Practice
                  </button>
                </div>
              </div>

              {/* High-Fidelity bar chart trend with custom tooltips matching screenshot 3 */}
              <div className="h-64 mt-8">
                {engagementDataset && engagementDataset.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={engagementDataset} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-800" />
                      <XAxis dataKey="name" tick={{ fontSize: 9, fontWeight: 'bold' }} stroke="#94a3b8" />
                      <YAxis scale="linear" domain={[0, 180]} ticks={[0, 45, 90, 135, 180]} tick={{ fontSize: 9, fontWeight: 'bold' }} stroke="#94a3b8" />
                      <Tooltip 
                        cursor={{ fill: '#f8fafc', opacity: 0.1 }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const dataItem = payload[0].payload;
                            return (
                              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-805 p-4 rounded-xl shadow-xl space-y-1 animate-fade-in border-l-4 border-indigo-500">
                                <p className="text-xs font-black text-slate-805 dark:text-slate-100 uppercase tracking-wider">{dataItem.name}</p>
                                <p className="text-[11px] font-extrabold text-indigo-600 dark:text-indigo-400">
                                  Completed Actions : {dataItem.progress}
                                </p>
                                <p className="text-[11px] font-bold text-slate-400">
                                  Passing Target : {dataItem.target}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }} 
                      />
                      <Bar dataKey="progress" fill="#6366f1" radius={[6, 6, 0, 0]} barSize={26} name="YOUR PROGRESS" />
                      <Bar dataKey="target" fill="#e2e8f0" radius={[6, 6, 0, 0]} barSize={26} className="dark:fill-slate-800" name="STATE TARGETS" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center opacity-30 gap-2">
                    <Activity size={32} />
                    <p className="text-xs font-bold tracking-wider uppercase">Loading engagement metrics...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Custom dynamic legend block matching screenshots 2 & 3 */}
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-[10px] sm:text-[11px] font-black uppercase tracking-wider text-slate-400 pt-5 mt-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#6366f1]" />
                <span className="text-slate-500 dark:text-slate-350">YOUR PROGRESS</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#e2e8f0] dark:bg-slate-800" />
                <span className="text-slate-500 dark:text-slate-350">STATE TARGETS</span>
              </div>
            </div>
          </div>

        </div>

        {/* Row 3: Gated Elite Forecasting & Cognitive Signature Radar vector */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Detailed Passing Forecast Widget (Gated lock state) */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] shadow-sm flex flex-col justify-between min-h-[460px] group relative overflow-hidden transition-all duration-350 hover:shadow-md">
            {/* Elegant Premium Blurred Block State for non-pro users */}
            {!isPro && (
              <div className="absolute inset-0 z-20 bg-slate-950/95 flex flex-col items-center justify-center p-8 text-center text-white">
                <div className="p-4 bg-gradient-to-tr from-rose-500 to-indigo-600 rounded-2xl mb-4 shadow-[0_5px_22px_rgba(244,63,94,0.3)] shrink-0 animate-bounce">
                  <Lock size={22} className="text-white fill-white" />
                </div>
                <h4 className="text-lg font-black uppercase tracking-wider text-rose-400">Detailed Passing Forecast Blocked</h4>
                <p className="text-slate-300 text-xs font-bold tracking-normal max-w-sm mt-3 uppercase leading-relaxed">
                  The Detailed Passing Forecast quadrant is currently compiled for PRO tier members. Join Elite Premium to run recursive AI passing forecast parameters, simulate accredited board probability percentages, and map milestone readiness.
                </p>
                <button 
                  onClick={() => navigate(`/${certId}/elite`)}
                  className={`mt-6 px-6 py-3.5 ${tokens.primaryBtn} text-white font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg transition-all active:scale-95`}
                >
                  Unlock AI Forecast Model
                </button>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">Detailed Passing Forecast</h3>
                  <p className="text-slate-400 text-xs font-bold tracking-widest uppercase mt-0.5">Recursive board passing forecast probability metrics</p>
                </div>
                <div className="p-3 bg-rose-50 dark:bg-rose-950 text-rose-605 rounded-2xl">
                  <ShieldCheck size={18} />
                </div>
              </div>

              <div className="min-h-[200px] flex flex-col justify-center my-6 space-y-4">
                <div className="p-4 bg-emerald-500/10 dark:bg-emerald-500/5 border border-emerald-500/20 rounded-2xl flex items-center gap-3">
                  <SunMedium className="text-emerald-500" size={24} />
                  <div>
                    <p className="text-[9px] font-black tracking-widest text-[#10b981] dark:text-emerald-400 uppercase">Board Calibrator Score</p>
                    <p className="text-sm font-black text-slate-805 dark:text-slate-200">Highly Probable Pass (Confidence Indicator: {analytics.readiness}%)</p>
                  </div>
                </div>

                <div className={`p-4 ${tokens.badgeBg} rounded-2xl border border-slate-200 dark:border-slate-800`}>
                  <div className="flex justify-between text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    <span>Accreditation Pass Rating</span>
                    <span>{analytics.readiness}% Ratio</span>
                  </div>
                  <div className="h-3.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden p-0.5">
                    <div className={`h-full rounded-full transition-all duration-1000 ${tokens.isClinical ? 'bg-[#0F7D6C]' : 'bg-gradient-to-r from-emerald-500 to-teal-500'}`} style={{ width: `${analytics.readiness}%` }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 dark:border-slate-800 pt-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <span>STATE ACCREDITED THRESHOLD: 85%</span>
              <span className="text-emerald-500 font-extrabold">{analytics.readiness >= 85 ? "STABLE PASS RANGE" : "CALIBRATING FOR PASS"}</span>
            </div>
          </div>

          {/* Cognitive Signature Radar Vector (Blown state lock overlay for non pro users) */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-8 shadow-sm flex flex-col justify-between min-h-[460px] relative transition-all duration-350 hover:shadow-md overflow-hidden">
            {/* Blown state overlay for non pro users */}
            {!isPro && (
              <div className="absolute inset-0 z-20 bg-slate-950/98 flex flex-col items-center justify-center p-8 text-center text-white">
                <div className="p-4 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-[0_5px_22px_rgba(99,102,241,0.355)] shrink-0 animate-pulse">
                  <BrainCircuit size={22} className="text-white" />
                </div>
                <h4 className="text-lg font-black uppercase tracking-wider text-rose-400">Cognitive Signature Blown</h4>
                <p className="text-slate-350 text-[11px] font-black tracking-normal max-w-sm mt-3 uppercase leading-relaxed text-slate-300">
                  Join Elite Pro to compile radar vectors mapping answer timings, clinical safety confidence profiles, and error margins for non pro users.
                </p>
                <button 
                  onClick={() => navigate(`/${certId}/elite`)}
                  className="mt-6 px-6 py-3 bg-amber-400 hover:bg-amber-500 text-slate-950 font-black text-xs uppercase tracking-widest rounded-2xl shadow-lg transition-all active:scale-95 border-none animate-pulse"
                >
                  Compile Cognitive Vector
                </button>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-black uppercase tracking-tight text-slate-900 dark:text-white">Cognitive Signature Radar Vector</h3>
                  <p className="text-slate-400 text-xs font-bold tracking-widest uppercase mt-0.5">Multi-dimensional cognitive feedback mapping answer timings, clinical safety profiles, and error limits</p>
                </div>
                <div className={`p-2.5 ${tokens.badgeBg} ${tokens.accentText} rounded-xl`}>
                  <Compass size={18} />
                </div>
              </div>

              <div className="h-60 mt-8 flex items-center justify-center relative">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="75%" data={cognitiveRadarData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fontWeight: 'bold', fill: '#94a3b8' }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar name="Cognitive Signature" dataKey="A" stroke={tokens.isClinical ? "#0F7D6C" : "#4f46e5"} fill={tokens.isClinical ? "#0F7D6C" : "#6366f1"} fillOpacity={0.4} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="flex justify-around items-center border-t border-slate-100 dark:border-slate-800 pt-5 text-[10px] sm:text-[11px] font-black text-slate-500 uppercase tracking-widest mt-4">
              <span>ELITE COGNITIVE SIGNATURE PIPELINE</span>
              <span className={`${tokens.accentText} font-extrabold animate-pulse`}>ACTIVE PSYCHOMETRICS</span>
            </div>
          </div>

        </div>

      </div>

      {/* Strategy Blueprint Milestone Completion Block */}
      {config.isStudyPlannerEnabled && user?.studyPlan?.isInitialized && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] p-8 shadow-md relative overflow-hidden group">
          <div className="absolute right-0 top-0 p-8 opacity-[0.03] group-hover:scale-105 transition-transform"><CalendarDays size={180}/></div>
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/35 text-emerald-800 dark:text-emerald-400 rounded-full text-[9px] font-black uppercase tracking-wider">
                <CheckCircle2 size={10} /> Active Blueprint Strategy
              </div>
              <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white uppercase">Blueprint Progression</h2>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-widest">
                Target Accredit Date: {new Date(user.studyPlan.targetDate).toLocaleDateString()}
              </p>
            </div>

            <div className="flex-1 max-w-md w-full space-y-2">
              <div className="flex justify-between text-xs font-black">
                <span className="text-slate-500 uppercase">Target Milestones Cleared</span>
                <span className={`${tokens.accentText} font-extrabold`}>
                  {Math.round((user.studyPlan.tasks.filter((t:any) => t.completed).length / user.studyPlan.tasks.length) * 100)}%
                </span>
              </div>
              <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden p-1 shadow-inner">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ${tokens.isClinical ? 'bg-[#0F7D6C]' : 'bg-gradient-to-r from-teal-500 to-indigo-600'}`}
                  style={{ width: `${Math.round((user.studyPlan.tasks.filter((t:any) => t.completed).length / user.studyPlan.tasks.length) * 100)}%` }}
                />
              </div>
            </div>
            
            <button 
              onClick={() => navigate(`/${certId}/dashboard`)}
              className={`px-6 py-3.5 ${tokens.primaryBtn} text-white rounded-2xl font-black text-[10px] tracking-widest uppercase transition-all shadow-md shrink-0 active:scale-95 flex items-center gap-2`}
            >
              <span>Full Strategy Blueprint</span>
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Actionable Suggestions & Personalized Focus Recommendation (The bottom spotlight card) */}
      <div className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] shadow-md flex flex-col md:flex-row items-center justify-between gap-6 relative group overflow-hidden`}>
        <div className="absolute right-0 bottom-0 w-80 h-80 bg-indigo-500/5 blur-[100px] pointer-events-none rounded-full" />
        
        <div className="flex items-center gap-5 relative z-10">
          <div className={`p-4 ${tokens.primaryBtn} text-white rounded-2xl shadow-xl shrink-0`}>
            <Brain size={28} className="text-white" />
          </div>
          <div>
            <h4 className="text-lg font-black tracking-tight text-slate-905 dark:text-slate-100 uppercase">{encouragementTips.title}</h4>
            <p className="text-slate-600 dark:text-slate-350 text-sm font-semibold max-w-2xl mt-1 leading-relaxed">
              {encouragementTips.desc}
            </p>
          </div>
        </div>

        <button 
          onClick={() => navigate(`/${certId}/${encouragementTips.actionPath}`)}
          className={`px-6 py-4 ${tokens.primaryBtn} font-black text-xs uppercase tracking-widest rounded-2xl transition-all shadow-lg active:scale-95 flex items-center gap-2 whitespace-nowrap relative z-10 shrink-0`}
        >
          <span>{encouragementTips.actionLabel}</span>
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform text-white" />
        </button>
      </div>
    </div>
  );
};
